import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { useState } from "react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "How does ThumbGenius work?",
      answer: "ThumbGenius uses advanced AI models trained on millions of high-performing YouTube thumbnails. Simply enter your video title or idea, choose a style and emotion, and our AI generates multiple thumbnail variations optimized for maximum click-through rate.",
    },
    {
      question: "Do I need design skills to use ThumbGenius?",
      answer: "Not at all! ThumbGenius is designed for creators of all skill levels. Our AI handles all the design work - composition, colors, text placement, and more. You just need to describe what you want.",
    },
    {
      question: "What makes ThumbGenius different from Canva or Photoshop?",
      answer: "Unlike traditional design tools, ThumbGenius is specifically built for YouTube thumbnails. Our AI understands what makes viewers click, automatically optimizes for the YouTube algorithm, and generates professional results in seconds rather than hours.",
    },
    {
      question: "How many thumbnails can I generate?",
      answer: "Free users get 5 generations per month. Pro users get unlimited generations. Each generation creates 4 thumbnail variations to choose from.",
    },
    {
      question: "What resolution are the thumbnails?",
      answer: "Free plan exports at 1280x720 (HD). Pro and Agency plans can export at up to 2560x1440 (4K) for crystal-clear quality on any device.",
    },
    {
      question: "Can I use my own images or branding?",
      answer: "Yes! Pro users can upload their own images, logos, and create brand kits with custom colors and fonts. The AI will incorporate your branding while maintaining optimal design principles.",
    },
    {
      question: "Is there a watermark on free plan thumbnails?",
      answer: "Yes, free plan exports include a small ThumbGenius watermark in the corner. Pro and Agency plans remove the watermark completely.",
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes, we offer a 7-day money-back guarantee on all paid plans. If you're not satisfied, contact our support team for a full refund.",
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Absolutely. You can cancel your subscription at any time from your account settings. You'll continue to have access until the end of your billing period.",
    },
    {
      question: "Are the generated thumbnails copyright-free?",
      answer: "Yes! All thumbnails generated by ThumbGenius are 100% original and yours to use. You have full commercial rights to use them on your YouTube channel and any other platform.",
    },
    {
      question: "How fast is the generation?",
      answer: "Most thumbnails are generated in under 30 seconds. Pro users get priority processing for even faster results during peak times.",
    },
    {
      question: "Do you offer team plans?",
      answer: "Yes! Our Agency plan includes 5 team seats with additional seats available. Contact our sales team for custom enterprise solutions.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="section-padding relative overflow-hidden">
          <div className="absolute inset-0 hero-glow" />
          <div className="container-custom relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <span className="text-sm font-semibold text-brand-purple uppercase tracking-wider mb-4 block">
                FAQ
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Frequently Asked{" "}
                <span className="gradient-text">Questions</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Got questions? We've got answers. If you can't find what you're looking for, 
                feel free to contact our support team.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section className="section-padding pt-0">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`rounded-2xl border transition-all duration-300 ${
                      openIndex === index
                        ? "border-primary/50 bg-card"
                        : "border-border bg-card/50 hover:border-primary/30"
                    }`}
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-6 text-left"
                    >
                      <span className="font-semibold pr-4">{faq.question}</span>
                      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        openIndex === index ? "bg-primary/20" : "bg-secondary"
                      }`}>
                        {openIndex === index ? (
                          <Minus className="w-4 h-4 text-primary" />
                        ) : (
                          <Plus className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                    {openIndex === index && (
                      <div className="px-6 pb-6">
                        <p className="text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center p-12 rounded-3xl bg-card border border-border relative overflow-hidden">
              <div className="absolute inset-0 hero-glow opacity-50" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-muted-foreground mb-8">
                  Our support team is here to help. Reach out anytime.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/signup">
                    <Button variant="hero" size="lg" className="group">
                      Get Started Free
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <a href="mailto:support@thumbgenius.ai">
                    <Button variant="outline" size="lg">
                      Contact Support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FAQ;
